import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { createPortal } from "react-dom";
import { BRAND, formatMoney, STORE_TIME_ZONE, type Order } from "@neus/shared";
import { api } from "../api/client";
import { CODE_QUERY, qrCode, qrPng } from "../barcodes";
import { CODE_FAILED, CODE_NOT_READY, CodeLoading, PrintButton } from "../components/CodeStatus";
import { PrintLogo } from "../components/layout/Logo";
import { features } from "../features";
import styles from "./Orders.module.css";

const message = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong. Please try again.");

/** "Oct 22, 2026": when the order was placed, in the store's time zone. */
export const placedOn = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: STORE_TIME_ZONE,
  });

/**
 * The link in a pickup order's QR code, the short link /api/qr/{number} (as on job order pick sheets): staff land on
 * the order's staff page, the shopper on their own order page.
 */
const qrUrl = (id: string) => `${window.location.origin}/api/qr/${encodeURIComponent(id)}`;

const storeAddress = () => {
  const { line1, city, state, postalCode } = BRAND.address;
  return `${line1}, ${city}, ${state} ${postalCode}`;
};

const subject = (order: Order) => `${BRAND.name} pickup order ${order.id}`;

/** The order as plain text, for a text message or an email. */
function orderText(order: Order) {
  const money = (cents: number) => formatMoney(cents, order.currency);
  return [
    `${subject(order)}, placed ${placedOn(order.createdAt)}`,
    ...order.lines.map((l) => `${l.qty} × ${l.name}: ${money(l.lineTotal)}`),
    `Total before tax: ${money(order.total)}`,
    "Not paid yet: pay at the register when you pick it up, and sales tax is added there.",
    `${BRAND.name}, ${storeAddress()}`,
    `Show this at the register: ${qrUrl(order.id)}`,
  ].join("\n");
}

function useQr(id: string) {
  return useQuery({ queryKey: ["pickup-qr", id], queryFn: () => qrCode(qrUrl(id)), ...CODE_QUERY });
}

/** The order's QR code on screen, for staff to scan at the register. */
export function PickupQr({ order }: { order: Order }) {
  const { data: qr, isPending } = useQr(order.id);
  // If it can't load, the Print button on this page says so.
  if (!qr && !isPending) return null;
  return (
    <figure className={styles.screenQr}>
      {qr ? <div dangerouslySetInnerHTML={{ __html: qr }} /> : <CodeLoading shape="qr" />}
      <figcaption className="muted">Show this at the register</figcaption>
    </figure>
  );
}

/**
 * The sheet the Print button prints (and the browser's own print): only this sheet prints, not the page. It sits
 * outside #root, which print hides.
 */
function PickupSheet({ order }: { order: Order }) {
  const { data: qr, isError } = useQr(order.id);
  const money = (cents: number) => formatMoney(cents, order.currency);
  return createPortal(
    <div className={styles.printSheet}>
      <header className={styles.printHead}>
        <PrintLogo className={styles.printLogo} />
        <div>
          <p>{BRAND.name} · Pick up in store, pay there</p>
          <h1>Order {order.id}</h1>
          <p>
            Placed {placedOn(order.createdAt)}
            {order.name && <> by {order.name}</>}
          </p>
          <p>
            {BRAND.name}, {storeAddress()}
          </p>
        </div>
        <figure className={styles.printQr}>
          {qr ? <div dangerouslySetInnerHTML={{ __html: qr }} /> : <p>{isError ? CODE_FAILED : CODE_NOT_READY}</p>}
          <figcaption>Show this at the register</figcaption>
        </figure>
      </header>
      <table className={styles.printLines}>
        <tbody>
          {order.lines.map((l) => (
            <tr key={l.sku}>
              <td>
                {l.qty} × {l.name}
              </td>
              <td>{money(l.lineTotal)}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">Total before tax</th>
            <td>{money(order.total)}</td>
          </tr>
        </tbody>
      </table>
      <p>Not paid yet: pay at the register when you pick it up, and sales tax is added there.</p>
    </div>,
    document.body,
  );
}

/** Whether this browser can share an image (phones, mostly): then Send offers the share sheet with the QR code. */
const canShareFiles = () => {
  try {
    return Boolean(navigator.canShare?.({ files: [new File([""], "qr.png", { type: "image/png" })] }));
  } catch {
    return false;
  }
};

/**
 * A pay-in-store pickup order's two buttons: Print (the sheet above, with the QR code) and Send, which opens the
 * ways to send it: the share sheet with the QR code image (where the browser can share files), a text message, and
 * an email (with pickupOrderEmails, sent by the API with the QR code inline; otherwise the mail app, with the link).
 */
export function PickupActions({ order }: { order: Order }) {
  const { isSuccess: qrReady, isError: qrFailed } = useQr(order.id);
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [share] = useState(canShareFiles);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const text = orderText(order);

  const shareQr = async () => {
    setStatus(null);
    try {
      const file = new File([await qrPng(qrUrl(order.id))], `${order.id}.png`, { type: "image/png" });
      await navigator.share({ title: subject(order), text, files: [file] });
    } catch (err) {
      // Closing the share sheet isn't a failure.
      if (err instanceof DOMException && err.name === "AbortError") return;
      setStatus({ ok: false, text: "Sharing didn't work here. Try a text message or email instead." });
    }
  };
  const email = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const { to } = await api.emailPickupOrder(order.id);
      setStatus({ ok: true, text: `Sent to ${to}, with the QR code.` });
    } catch (err) {
      setStatus({ ok: false, text: message(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.actions}>
      <div className={styles.actionRow}>
        <PrintButton ready={qrReady} failed={qrFailed} />
        <button
          type="button"
          className="btn btn--ghost"
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => setOpen(!open)}
        >
          Send
        </button>
      </div>
      {open && (
        <div id={menuId} className={styles.actionRow}>
          {share && (
            <button type="button" className="btn" onClick={() => void shareQr()}>
              Share with QR code
            </button>
          )}
          <a className="btn btn--ghost" href={`sms:?&body=${encodeURIComponent(text)}`}>
            Text message
          </a>
          {features.pickupOrderEmails ? (
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void email()}>
              {busy ? "Sending…" : "Email it to me"}
            </button>
          ) : (
            <a
              className="btn btn--ghost"
              href={`mailto:?subject=${encodeURIComponent(subject(order))}&body=${encodeURIComponent(text)}`}
            >
              Email
            </a>
          )}
        </div>
      )}
      {status && (
        <div className={status.ok ? "notice" : "notice notice--warn"} role="status">
          {status.text}
        </div>
      )}
      <PickupSheet order={order} />
    </div>
  );
}
